// src/pages/Enseignants/ListingAdherents/utils/listingAdherentsUtils.js

import getCategory from "../../../../../utils/getCategory";
import getWeightCategory from "../../../../../utils/getWeightCategory";

export const seasonNeighbors = (season) => {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(String(season || ""));

  if (!match) {
    return { prev: "", next: "" };
  }

  const start = Number(match[1]);
  const end = Number(match[2]);

  return {
    prev: `${start - 1}-${end - 1}`,
    next: `${start + 1}-${end + 1}`,
  };
};

export const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

export const toAbsolute = (url) => {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${value}`;
};

export const getAdherentPhoto = (adherent) => {
  if (adherent?.autorisation_photo === false) {
    return "/img/interdiction.jpg";
  }

  return (
    toAbsolute(
      adherent?.photo_url ||
        adherent?.photoURL ||
        adherent?.photo ||
        adherent?.avatar
    ) || getDefaultPhoto(adherent?.genre)
  );
};

export const hasUsablePhoto = (adherent) => {
  if (adherent?.autorisation_photo === false) return false;

  return Boolean(
    adherent?.photo_url ||
      adherent?.photoURL ||
      adherent?.photo ||
      adherent?.avatar
  );
};

export const calculateAge = (dateNaissance) => {
  if (!dateNaissance) return null;

  const birth = new Date(dateNaissance);
  if (Number.isNaN(birth.getTime())) return null;

  const today = new Date();

  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birth.getDate())
  ) {
    age -= 1;
  }

  return age;
};

export const formatDateFR = (dateValue) => {
  if (!dateValue) return "—";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
};

export const getGroupsLabel = (adherent) => {
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

  return groups
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
};

export const getPrimaryGroupName = (adherent) => {
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];
  return groups?.[0]?.nom || "Sans groupe";
};

export const getStatusMeta = (status) => {
  switch (status) {
    case "Licencié":
      return {
        label: status,
        dot: "bg-emerald-500",
        className:
          "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800/50",
      };

    case "Licencié Hors Club":
      return {
        label: status,
        dot: "bg-teal-500",
        className:
          "bg-teal-100 text-teal-800 ring-1 ring-teal-200 dark:bg-teal-900/30 dark:text-teal-200 dark:ring-teal-800/50",
      };

    case "Inscrit":
      return {
        label: status,
        dot: "bg-amber-500",
        className:
          "bg-amber-100 text-amber-900 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800/50",
      };

    case "Cours d'essai":
      return {
        label: status,
        dot: "bg-orange-500",
        className:
          "bg-orange-100 text-orange-900 ring-1 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-200 dark:ring-orange-800/50",
      };

    case "Licencié - Arrêt":
    case "Non Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return {
        label: status,
        dot: "bg-slate-500",
        className:
          "bg-slate-200 text-slate-800 ring-1 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
      };

    case "Ancien Licencié":
      return {
        label: status,
        dot: "bg-slate-900",
        className:
          "bg-slate-900 text-white ring-1 ring-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:ring-slate-300",
      };

    default:
      return {
        label: status || "Sans statut",
        dot: "bg-slate-400",
        className:
          "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      };
  }
};

export const extractEmailsFromAdherent = (adherent) => {
  const fields = [
    "email",
    "mail",
    "email_parent",
    "email_responsable",
    "mail_parent",
    "mail_responsable",
    "email_pere",
    "email_mere",
    "mail_pere",
    "mail_mere",
  ];

  const emails = new Set();

  fields.forEach((field) => {
    const value = adherent?.[field];
    if (!value) return;

    String(value)
      .split(/[;,/\s]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((email) => {
        if (/.+@.+\..+/.test(email)) {
          emails.add(email);
        }
      });
  });

  return Array.from(emails);
};

const fullName = (adherent) =>
  `${adherent?.nom || ""} ${adherent?.prenom || ""}`.trim();

const ageValue = (adherent) => {
  const age = calculateAge(adherent?.date_naissance);
  return Number.isFinite(age) ? age : Number.POSITIVE_INFINITY;
};

export const sorters = {
  alpha: (a, b) => fullName(a).localeCompare(fullName(b), "fr"),
  "alpha-desc": (a, b) => fullName(b).localeCompare(fullName(a), "fr"),
  "age-asc": (a, b) => ageValue(a) - ageValue(b),
  "age-desc": (a, b) => ageValue(b) - ageValue(a),
  statut: (a, b) =>
    String(a?.statut_inscription || "").localeCompare(
      String(b?.statut_inscription || ""),
      "fr"
    ),
};

export const getAgeCategoryForSeason = (dateOfBirth, season) => {
  if (!dateOfBirth || !season) {
    return getCategory(dateOfBirth) || "Inconnu";
  }

  const birthYear = new Date(dateOfBirth).getFullYear();
  const startYear = parseInt(String(season).split("-")[0], 10);

  if (!birthYear || !startYear) return getCategory(dateOfBirth) || "Inconnu";

  if (birthYear >= startYear - 5 && birthYear <= startYear - 3) return "Eveil";
  if (birthYear >= startYear - 7 && birthYear <= startYear - 6) return "Mini-Poussin(e)";
  if (birthYear >= startYear - 9 && birthYear <= startYear - 8) return "Poussin(e)";
  if (birthYear >= startYear - 11 && birthYear <= startYear - 10) return "Benjamin(e)";
  if (birthYear >= startYear - 13 && birthYear <= startYear - 12) return "Minime";
  if (birthYear >= startYear - 16 && birthYear <= startYear - 14) return "Cadet(te)";
  if (birthYear >= startYear - 19 && birthYear <= startYear - 17) return "Junior";
  if (birthYear >= startYear - 29 && birthYear <= startYear - 20) return "Sénior";
  if (birthYear <= startYear - 30) return "Vétéran";

  return getCategory(dateOfBirth) || "Inconnu";
};

export const normalizeAgeCategoryForWeight = (ageCategory) => {
  const value = String(ageCategory || "");

  const map = {
    "Eveil": "Eveil",
    "Mini-Poussin(e)": "Mini-Poussin",
    "Mini-Poussin": "Mini-Poussin",
    "Poussin(e)": "Poussin",
    "Poussin": "Poussin",
    "Benjamin(e)": "Benjamin",
    "Benjamin": "Benjamin",
    "Minime": "Minime",
    "Cadet(te)": "Cadet",
    "Cadet": "Cadet",
    "Junior": "Junior",
    "Sénior": "Sénior",
    "Senior": "Sénior",
    "Vétéran": "Vétéran",
    "Veteran": "Vétéran",
  };

  return map[value] || value;
};

export const getAdherentCategories = (adherent) => {
  const ageCategory = getAgeCategoryForSeason(
    adherent?.date_naissance,
    adherent?.saison_active
  );

  const normalizedAgeCategory = normalizeAgeCategoryForWeight(ageCategory);

  const weightCategory = getWeightCategory(
    adherent?.genre,
    normalizedAgeCategory,
    adherent?.poids
  );

  return {
    ageCategory,
    normalizedAgeCategory,
    weightCategory,
  };
};